import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    DEFAULT_AUDIT_SORT,
    EMPTY_AUDIT_FILTERS,
    countActiveAuditFilters,
    type AuditFilters,
    type AuditSort,
    type AuditSortField,
} from './auditFilterTypes';
import { AUDIT_DATE_PRESET_ORDER, type AuditDatePreset } from './filters/auditDatePresets';

/**
 * EAA-1821: the audit screen's filter/sort/page state, with the URL as the
 * single source of truth.
 *
 * URL-backed rather than `useState` because an audit trail is something people
 * SEND each other ("look at these three failed resets"): before this, every
 * filter lived in component state, so the only shareable thing about a narrowed
 * table was a screenshot, and a reload dropped the operator back to page 1 of
 * everything.
 */
export interface UseAuditFiltersResult {
    /** Applied immediately — what the controls render. */
    filters: AuditFilters;
    setFilters: (next: AuditFilters) => void;
    sort: AuditSort;
    /** Toggles direction when the same column is clicked again. */
    setSortField: (field: AuditSortField) => void;
    page: number;
    setPage: (next: number) => void;
    /** What the request should use — free-text fields lag by `TEXT_DEBOUNCE_MS`. */
    debouncedFilters: AuditFilters;
    activeCount: number;
    clear: () => void;
}

/**
 * EAA-1821: every key this hook owns. Listed once so a write can DELETE the
 * whole set before re-adding only the non-default ones — the alternative
 * (setting each key) leaves a stale `from`/`to` behind the moment the operator
 * moves off the custom preset, and the table would keep filtering by a date
 * range no control on screen still shows.
 */
const AUDIT_URL_KEYS = [
    'q',
    'action',
    'success',
    'mode',
    'target_type',
    'target',
    'actor',
    'preset',
    'from',
    'to',
    'sort',
    'page',
] as const;

const SORT_FIELDS: AuditSortField[] = ['created_at', 'action', 'success'];

/** Only the free-text boxes wait; a chip click is a deliberate act and applies at once. */
const TEXT_DEBOUNCE_MS = 300;

const EMPTY_LIST: string[] = [];

/**
 * EAA-1821: content-keyed so an unchanged comma-string returns the SAME array
 * instance every time.
 *
 * `filters` is rebuilt whenever the query string changes for any reason, and
 * the free-text boxes write to the URL on every keystroke (they apply
 * immediately; only the FETCH is debounced). A fresh `split()` array per call
 * therefore gave `action`/`credentialMode`/`targetType` a new identity on each
 * keystroke even though nothing about those facets had changed — which defeated
 * `React.memo` on `AuditTable` and on every facet chip, making the whole table
 * body re-render per keystroke instead of per debounce tick. The cache is
 * unbounded but keyed by URL-sized strings from a fixed set of params, so it
 * cannot grow meaningfully.
 */
const listCache = new Map<string, string[]>();

function parseList(raw: string | null): string[] {
    if (!raw) return EMPTY_LIST;
    const cached = listCache.get(raw);
    if (cached) return cached;
    const parsed = raw
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean);
    listCache.set(raw, parsed);
    return parsed;
}

function parsePreset(raw: string | null): AuditDatePreset {
    return AUDIT_DATE_PRESET_ORDER.find((preset) => preset === raw) ?? 'all';
}

function parseSort(raw: string | null): AuditSort {
    if (!raw) return DEFAULT_AUDIT_SORT;
    const direction = raw.startsWith('-') ? 'desc' : 'asc';
    const field = (raw.startsWith('-') ? raw.slice(1) : raw) as AuditSortField;
    // EAA-1821: an unknown field would be sent straight to the backend's
    // `ordering` whitelist and silently ignored there, leaving the header
    // showing a sort the rows do not have. Fall back instead.
    if (!SORT_FIELDS.includes(field)) return DEFAULT_AUDIT_SORT;
    return { field, direction };
}

function parsePage(raw: string | null): number {
    const parsed = Number.parseInt(raw ?? '', 10);
    if (!Number.isFinite(parsed) || parsed < 1) return 1;
    return parsed;
}

/**
 * The raw query values this hook reads, already pulled off `URLSearchParams`.
 *
 * EAA-1821: takes a plain bag rather than the `URLSearchParams` itself so the
 * caller can list each value as its own `useMemo` dependency — see the comment
 * in `useAuditFilters` on why keying off the whole serialized query defeated
 * every downstream `React.memo`.
 */
interface AuditFilterParams {
    q: string | null;
    action: string | null;
    success: string | null;
    mode: string | null;
    target_type: string | null;
    target: string | null;
    actor: string | null;
    preset: string | null;
    from: string | null;
    to: string | null;
}

function readFilters(params: AuditFilterParams): AuditFilters {
    const preset = parsePreset(params.preset);
    return {
        search: params.q ?? '',
        action: parseList(params.action),
        success: params.success === 'true' || params.success === 'false' ? params.success : '',
        credentialMode: parseList(params.mode),
        targetType: parseList(params.target_type),
        target: params.target ?? '',
        actor: params.actor ?? '',
        date:
            preset === 'custom'
                ? {
                      preset,
                      from: params.from ?? undefined,
                      to: params.to ?? undefined,
                  }
                : { preset },
    };
}

function writeFilters(
    params: URLSearchParams,
    filters: AuditFilters,
    sort: AuditSort,
    page: number
): void {
    AUDIT_URL_KEYS.forEach((key) => params.delete(key));

    if (filters.search.trim()) params.set('q', filters.search);
    if (filters.action.length) params.set('action', filters.action.join(','));
    if (filters.success) params.set('success', filters.success);
    if (filters.credentialMode.length) params.set('mode', filters.credentialMode.join(','));
    if (filters.targetType.length) params.set('target_type', filters.targetType.join(','));
    if (filters.target.trim()) params.set('target', filters.target);
    if (filters.actor.trim()) params.set('actor', filters.actor);
    if (filters.date.preset !== 'all') params.set('preset', filters.date.preset);
    if (filters.date.preset === 'custom') {
        if (filters.date.from) params.set('from', filters.date.from);
        if (filters.date.to) params.set('to', filters.date.to);
    }
    if (
        sort.field !== DEFAULT_AUDIT_SORT.field ||
        sort.direction !== DEFAULT_AUDIT_SORT.direction
    ) {
        params.set('sort', sort.direction === 'desc' ? `-${sort.field}` : sort.field);
    }
    if (page > 1) params.set('page', String(page));
}

/**
 * EAA-1821: a value-level debounce, not the kit's `useDebouncedSearch` — that
 * hook owns an input's local state and hands back an `onChange`, which does not
 * fit here: the text lives in the URL (so it survives reload and is shareable)
 * and three separate fields share one delay. Debouncing the VALUE keeps the
 * controls perfectly responsive and delays only the fetch.
 */
function useDebouncedValue<T>(value: T, delay: number): T {
    const [debounced, setDebounced] = useState(value);

    useEffect(() => {
        const timer = window.setTimeout(() => setDebounced(value), delay);
        return () => window.clearTimeout(timer);
    }, [value, delay]);

    return debounced;
}

export function useAuditFilters(): UseAuditFiltersResult {
    const [searchParams, setSearchParams] = useSearchParams();

    // EAA-1821: memoize on the individual PARAM VALUES, not on the
    // `searchParams` object and not on its serialized string.
    //
    // The object is out because the router hands back a fresh instance on
    // unrelated re-renders. The whole string is out for a subtler reason: the
    // free-text boxes write to the URL on every keystroke (they apply at once —
    // only the FETCH is debounced), so a single `toString()` key gave `filters`,
    // `sort` AND `page` a new identity per keystroke, including when the change
    // was `?source=` or `?page=` and nothing about the filters moved. Every
    // `React.memo` downstream — `AuditTable`, every facet chip, every column
    // control — then bailed out on each keystroke, which is precisely what
    // those memos were added to prevent.
    //
    // Keying each derivation on the params it actually reads means a facet chip
    // re-renders when ITS facet changes and not when a sibling's does.
    const qParam = searchParams.get('q');
    const actionParam = searchParams.get('action');
    const successParam = searchParams.get('success');
    const modeParam = searchParams.get('mode');
    const targetTypeParam = searchParams.get('target_type');
    const targetParam = searchParams.get('target');
    const actorParam = searchParams.get('actor');
    const presetParam = searchParams.get('preset');
    const fromParam = searchParams.get('from');
    const toParam = searchParams.get('to');
    const sortParam = searchParams.get('sort');
    const pageParam = searchParams.get('page');

    const filters = useMemo(
        () =>
            readFilters({
                q: qParam,
                action: actionParam,
                success: successParam,
                mode: modeParam,
                target_type: targetTypeParam,
                target: targetParam,
                actor: actorParam,
                preset: presetParam,
                from: fromParam,
                to: toParam,
            }),
        [
            qParam,
            actionParam,
            successParam,
            modeParam,
            targetTypeParam,
            targetParam,
            actorParam,
            presetParam,
            fromParam,
            toParam,
        ]
    );
    const sort = useMemo(() => parseSort(sortParam), [sortParam]);
    const page = useMemo(() => parsePage(pageParam), [pageParam]);

    const commit = useCallback(
        (nextFilters: AuditFilters, nextSort: AuditSort, nextPage: number) => {
            setSearchParams(
                (prev) => {
                    // EAA-1821: start from the CURRENT params so sibling state
                    // this hook does not own — `?source=account|admin` on the
                    // audit page, the accounts page's `?tab=` — survives a
                    // filter change instead of being dropped.
                    const params = new URLSearchParams(prev);
                    writeFilters(params, nextFilters, nextSort, nextPage);
                    return params;
                },
                // Typing in the search box would otherwise push one history
                // entry per keystroke, making Back unusable.
                { replace: true }
            );
        },
        [setSearchParams]
    );

    // EAA-1821: any narrowing resets the page. Staying on page 7 of a result
    // set that just shrank to two pages shows an empty table that looks like
    // "no matching records" — the filter appears broken when it worked.
    const setFilters = useCallback((next: AuditFilters) => commit(next, sort, 1), [commit, sort]);

    const setSortField = useCallback(
        (field: AuditSortField) => {
            const next: AuditSort =
                sort.field === field
                    ? { field, direction: sort.direction === 'asc' ? 'desc' : 'asc' }
                    : // A first click on time means "newest first"; on a
                      // categorical column it means A→Z.
                      { field, direction: field === 'created_at' ? 'desc' : 'asc' };
            commit(filters, next, 1);
        },
        [commit, filters, sort]
    );

    const setPage = useCallback(
        (next: number) => commit(filters, sort, Math.max(1, next)),
        [commit, filters, sort]
    );

    const clear = useCallback(() => commit(EMPTY_AUDIT_FILTERS, sort, 1), [commit, sort]);

    const debouncedSearch = useDebouncedValue(filters.search, TEXT_DEBOUNCE_MS);
    const debouncedTarget = useDebouncedValue(filters.target, TEXT_DEBOUNCE_MS);
    const debouncedActor = useDebouncedValue(filters.actor, TEXT_DEBOUNCE_MS);

    const debouncedFilters = useMemo<AuditFilters>(
        () => ({
            ...filters,
            search: debouncedSearch,
            target: debouncedTarget,
            actor: debouncedActor,
        }),
        [filters, debouncedSearch, debouncedTarget, debouncedActor]
    );

    const activeCount = useMemo(() => countActiveAuditFilters(filters), [filters]);

    return {
        filters,
        setFilters,
        sort,
        setSortField,
        page,
        setPage,
        debouncedFilters,
        activeCount,
        clear,
    };
}
